// The fake api's provider status page (F-HQ-38, spec/14 §15), past the HQ guard in `fake-server.ts` (`hq:settings`):
// every provider with its configuration and last check, and a test that records a result. Email and IGDB are
// configured and testable; IGDB's check fails (to show an error); SMS is configured but has no safe check; the rest
// are not configured. Bodies are the contract's own shapes.

import type { HqProvider } from '@clavian/api-client';
import { HqProviderSchema } from '@clavian/contracts';

import type { CatalogueHelpers } from './fake-hq-catalogue';
import type { FakeResponse } from './fake-server';

const NOW = '2026-10-01T09:00:00.000Z';

function provider(overrides: Partial<HqProvider> & Pick<HqProvider, 'id' | 'name' | 'purpose'>): HqProvider {
  return HqProviderSchema.parse({
    driver: null,
    status: 'not_configured',
    testable: false,
    details: [],
    lastCheckedAt: null,
    lastLatencyMs: null,
    lastError: null,
    ...overrides,
  });
}

export function createFakeHqProviders() {
  const state = {
    providers: [
      provider({
        id: 'email',
        name: 'Email',
        purpose: 'Sign-in codes, staff invitations, receipts and alerts by email.',
        driver: 'smtp',
        status: 'configured',
        testable: true,
        details: [
          { label: 'Host', value: 'smtp.resend.com' },
          { label: 'Port', value: '587' },
          { label: 'Sender domain', value: 'mail.clavian.io' },
        ],
      }),
      provider({ id: 'sms', name: 'SMS', purpose: 'Sign-in codes and notifications by text message.', driver: 'fake', status: 'configured' }),
      provider({ id: 'whatsapp', name: 'WhatsApp', purpose: 'Sign-in codes by WhatsApp.' }),
      provider({
        id: 'igdb',
        name: 'IGDB',
        purpose: 'Game metadata and covers for the catalogue (through Twitch).',
        driver: 'twitch',
        status: 'configured',
        testable: true,
      }),
      provider({ id: 'steamgriddb', name: 'SteamGridDB', purpose: 'Hero images and logos for the catalogue.' }),
      provider({ id: 'payments', name: 'Payments', purpose: 'Online payments for top-ups and sales.' }),
      provider({ id: 'objectStorage', name: 'Object storage', purpose: 'Uploads, ID photos, venue photos, game art and station media.' }),
      provider({
        id: 'realtime',
        name: 'Realtime',
        purpose: 'The live link to stations, kiosks and consoles (Valkey pub/sub and presence).',
        driver: 'valkey',
        status: 'configured',
        testable: true,
      }),
    ],
    tests: [] as string[],
  };

  function handle(helpers: CatalogueHelpers, method: string, pathname: string): FakeResponse | undefined {
    const { json, problem } = helpers;
    if (pathname === '/v1/hq/providers' && method === 'GET') return json(200, { data: state.providers });
    const test = /^\/v1\/hq\/providers\/([^/]+)\/test$/.exec(pathname);
    if (test !== null && method === 'POST') {
      const index = state.providers.findIndex((one) => one.id === test[1]);
      const found = state.providers[index];
      if (found === undefined) return problem(400, 'validation_failed', 'The request is not valid.');
      if (!found.testable) return problem(409, 'conflict', 'The provider is not configured, or it has no side-effect-free check.');
      state.tests.push(found.id);
      const failed = found.id === 'igdb';
      const updated: HqProvider = {
        ...found,
        status: failed ? 'unreachable' : 'reachable',
        lastCheckedAt: NOW,
        lastLatencyMs: failed ? 1500 : 120,
        lastError: failed ? 'Twitch refused the client credentials (401).' : null,
      };
      state.providers[index] = updated;
      return json(200, updated);
    }
    return undefined;
  }

  return { state, handle };
}
